'use client';

import { fetchCustomerAccount } from '@/api/account';
import { toast } from '@/components/Toast';
import { updateConsentsAfterAuth } from '@/hooks/useComplianceConsent';
import useLoginSuccess from '@/hooks/useLoginSuccess';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { IS_US } from '@/utils/constants';
import { getGroupInfo, getProductInfo } from '@/utils/feature-value';
import { useRouter } from 'next/navigation';
import { useCallback } from 'react';

type ThirdLoginMethodPayload = {
  login_type: string;
  login_method: string;
  sceneCode?: string;
};

interface ThirdLoginParams {
  token?: string;
  store_id?: string;
  isRegister?: string;
  registerType?: string;
  eventType?: string;
  third_login_msg?: string;
}

/**
 * 第三方登录成功处理Hook
 * 迁移自Vue项目的checkThirdLogin方法
 */
export const useThirdPartyLogin = () => {
  const router = useRouter();
  const { handleLoginSuccess } = useLoginSuccess();
  const { trackEvent } = useReporting();
  const { cnName } = useRouteMetadata();
  const inAppH5 = useStore((state) => state.inAppH5);
  const groupInfo = getGroupInfo();
  const productInfo = getProductInfo();
  const getFromRouteInfo = useStore((state) => state.getFromRouteInfo);
  const setToken = useStore((state) => state.setToken);

  /**
   * 神策埋点：登录成功结果
   * 迁移自Vue项目的SDLoginSuccessResult方法
   * @TrackType sensorsdata
   * @EventType LoginSuccess_Result
   * @EventDesc 登录成功结果
   * @EventParams {
   *  is_new: 是否新注册,
   *  login_situation: 登录场景,
   *  url_path: 登录场景的页面路径,
   *  login_type: 登录方式
   *  group_id: 前置分组 id
   *  product_sku: 魔晶商品 SKU
   *  shop_sku： 网站商品 SKU
   * }
   * @Tapd 1008875
   */
  const trackLoginSuccessResult = useCallback(
    (is_new: boolean, login_type: string) => {
      try {
        let thirdLoginType: any = {};
        let loginSituationNew = '';

        // 从 sessionStorage 获取登录情况数据
        if (typeof window !== 'undefined') {
          const storedThirdLoginMethod = sessionStorage.getItem('zl-third-login-method');
          if (storedThirdLoginMethod) {
            try {
              thirdLoginType = JSON.parse(storedThirdLoginMethod);
            } catch (e) {
              logger.error('Failed to parse third login method:', e);
            }
          }

          const storedLoginSituationNew = sessionStorage.getItem('zl-login-situation-new');
          if (storedLoginSituationNew) {
            loginSituationNew = storedLoginSituationNew;
          }
        }

        const sdEventParams = {
          is_new,
          url_path: typeof window !== 'undefined' ? window.location.href : '',
          login_method: thirdLoginType.login_method || '',
          account_type: '三方账号',
          account_platform: thirdLoginType.login_type || '',
          login_type,
          group_id: groupInfo.group_id,
          product_sku: productInfo.product_sku,
          shop_sku: productInfo.shop_sku,
          page_name: cnName || '其他',
          login_situation_new: loginSituationNew || '其他',
        };

        trackEvent(
          {
            eventName: 'LoginSuccess_Result',
            properties: sdEventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('LoginSuccess_Result tracking error:', error);
      }
    },
    [trackEvent, cnName, groupInfo, productInfo, getFromRouteInfo],
  );

  /**
   * 检查第三方登录成功
   * 从URL参数中获取登录信息并处理
   */
  const checkThirdLogin = useCallback(async () => {
    // 获取URL参数的辅助函数
    const getQueryParam = (name: string): string | null => {
      if (typeof window === 'undefined') {
        return null;
      }
      return new URLSearchParams(window.location.search).get(name);
    };

    const params: ThirdLoginParams = {
      token: getQueryParam('token'),
      store_id: getQueryParam('store_id'),
      isRegister: getQueryParam('isRegister'),
      registerType: getQueryParam('registerType'),
      eventType: getQueryParam('eventType'),
      third_login_msg: getQueryParam('third_login_msg'),
    };
    logger.warn('ThirdLoginParams', params);

    try {
      /* 登录成功处理 */
      const token = params.token;
      if (token) {
        setToken(token);
        // 获取用户账户信息
        const accountResponse = await fetchCustomerAccount();
        if (accountResponse.code === 200 && accountResponse.data) {
          const userData = accountResponse.data;

          // 延迟执行登录后的操作（如购物车同步等）
          setTimeout(async () => {
            await handleLoginSuccess({
              token,
              customer: userData,
              msg: !params.store_id ? 'Login Success' : '',
            });

            if (IS_US && typeof window !== 'undefined') {
              const raw = sessionStorage.getItem('zl-third-login-method');
              if (raw) {
                try {
                  const parsed = JSON.parse(raw) as ThirdLoginMethodPayload;
                  const sceneCode = parsed.sceneCode;
                  if (sceneCode) {
                    delete parsed.sceneCode;
                    sessionStorage.setItem('zl-third-login-method', JSON.stringify(parsed));
                    await updateConsentsAfterAuth({
                      sceneCode,
                      page: cnName,
                      extra: { email: userData.email },
                    });
                  }
                } catch (e) {
                  logger.error('Third-party login compliance scene parse failed:', e);
                }
              }
            }

            // TODO 老站有个 this.userSource(); 还未处理。 待跟后端确认过之后再调用
            // 三方登录成功后的埋点。注：有另外一部分埋点在 userSlice.ts 中处理
            trackEvent(
              {
                eventName: 'sign_up',
                properties: {
                  method: params.registerType,
                  user_id: `${userData.id}`,
                },
              },
              [ProviderType.APPLOVIN],
            );
            if (params.isRegister === '1') {
              trackEvent(
                undefined,
                [ProviderType.SNAPCHAT, ProviderType.SNAPCHAT_CONVERSION],
                { userInfo: userData },
                'signup.success',
              );
            }
          }, 0);

          // 第三方登录成功埋点 - 只在非App环境中触发
          if (!inAppH5) {
            // 延迟上报：https://alidocs.dingtalk.com/i/nodes/amweZ92PV6vw1LdxI9rZZ1B0VxEKBD6p
            // TODO: 登录后的一些操作还可以再继续优化，统一节奏，现在散落各地！！
            setTimeout(() => {
              trackLoginSuccessResult(params.isRegister === '1', params.registerType);
            }, 1000);
          }
        }
        // 清除URL中的token参数
        await clearTokenFromUrl();
      }

      /* 登录失败处理 */
      if (params.third_login_msg) {
        toast.error(params.third_login_msg);
      }
    } catch (error) {
      logger.error('Third party login processing failed:', error);
    }
  }, []);

  /**
   * 清除URL中的token参数
   */
  const clearTokenFromUrl = async () => {
    try {
      const url = new URL(window.location.href);
      const hasToken = url.searchParams.has('token');

      if (hasToken) {
        // 移除token相关参数
        const paramsToRemove = [
          'token',
          'store_id',
          'isRegister',
          'registerType',
          'eventType',
          'third_login_msg',
        ];
        paramsToRemove.forEach((param) => {
          url.searchParams.delete(param);
        });

        // 更新URL（不会触发页面刷新）
        const newUrl = url.pathname + (url.search ? url.search : '');
        router.replace(newUrl, { scroll: false });
      }
    } catch (error) {
      logger.error('Failed to clear token from URL:', error);
    }
  };

  /**
   * 初始化时检查第三方登录 TODO 在 GlobalDialog 里面调用这里的方法
   */
  // useEffect(() => {
  //   // 检查是否有第三方登录相关参数
  //   const hasThirdLoginParams = searchParams.has('token') || searchParams.has('third_login_msg');

  //   if (hasThirdLoginParams) {
  //     checkThirdLogin();
  //   }
  // }, [checkThirdLogin, searchParams]);

  return {
    checkThirdLogin,
    trackLoginSuccessResult,
  };
};

export default useThirdPartyLogin;
